import { styles as sharedStyles } from "./shared.styles";
import {
  getTheme,
  Link,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import EV2ExperimentsContent from "./EV2ExperimentsContent";
import { useGetFeatureParam } from "../Hooks/UseFeature";
import { AcceptedFeatures, AppName } from "./shared.consts";
import PreserveParamsLink from "./LinkComponents/PreserveParamsLink";
import { useFetchBaseJsonConfig } from "../Hooks/AdoHooks/UseFetchBaseJsonConfig";
import useAppInsights from "../Hooks/UseAppInsights";
import { NavType } from "./AppInsights";
import { useEffect } from "react";
import { useAppContext } from "../Contexts";

/**
 * Props for the EV2Experiments component
 */
interface EV2ExperimentsProps {
  /**
   * When true show experiment creation form
   */
  isCreate?: boolean;
  /**
   * When true show experiment cloning form with pre-filled data
   */
  isClone?: boolean;
  /**
   * When true will direct users to select an extension.
   */
  isRoot?: boolean;
}

const EV2Experiments: React.FC<EV2ExperimentsProps> = ({
  isCreate,
  isClone,
  isRoot,
}) => {
  const { setExtensionConfig } = useAppContext();
  const hideTopBar = useGetFeatureParam(AcceptedFeatures.hideTopBar.key);
  const { loading: extensionConfigLoading, value: extensionConfig } =
    useFetchBaseJsonConfig();
  const { trackNavigationEvent } = useAppInsights("EV2Experiments");

  useEffect(() => {
    if (!extensionConfigLoading && extensionConfig) {
      setExtensionConfig(extensionConfig);
    }
  }, [extensionConfigLoading, extensionConfig, setExtensionConfig]);

  if (extensionConfigLoading) {
    return (
      <div className={sharedStyles.loadingSpinner}>
        <Spinner
          label="Loading extension details..."
          size={SpinnerSize.large}
        />
      </div>
    );
  }

  return (
    <Stack
      className={sharedStyles.pageStyles}
      data-testid="ev2-experiments-page"
    >
      {hideTopBar && (
        <Stack tokens={{ childrenGap: 20 }}>
          <Text
            variant="xxLarge"
            className={styles.contextAreaText}
            data-testid="app-title-text"
          >
            <PreserveParamsLink className={styles.link} to="/">
              {AppName}
            </PreserveParamsLink>
          </Text>
          <MessageBar
            className={styles.contextAreaText}
            messageBarType={MessageBarType.info}
          >
            This hub is used to manage and monitor EV2 experiments for{" "}
            <strong>Azure Portal Extensions</strong>. For onboarding follow the{" "}
            <Link
              href="https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackNavigationEvent({
                  destination:
                    "https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2",
                  navType: NavType.External,
                });
              }}
            >
              Onboard to Experimentation via EV2
            </Link>{" "}
            guide and in case of any questions or issues, please contact
            <Link
              href="mailto:ibizafxhot@microsoft.com"
              onClick={() => {
                trackNavigationEvent({
                  destination: "mailto:ibizafxhot@microsoft.com",
                  navType: NavType.External,
                });
              }}
            >
              ibizafxhot@microsoft.com
            </Link>
            .
          </MessageBar>
        </Stack>
      )}
      {!extensionConfigLoading && !extensionConfig && (
        <MessageBar messageBarType={MessageBarType.severeWarning}>
          Unable to load extension configurations. Reload page and open{" "}
          <em>Extension Details</em> to verify extension configs are present. If
          missing, use <em>Extension Details</em> form to add extension
          configurations, without these configurations experiments may not run
          correctly.
        </MessageBar>
      )}
      <EV2ExperimentsContent
        isCreate={isCreate}
        isClone={isClone}
        isRoot={isRoot}
      />
    </Stack>
  );
};

const theme = getTheme();

const styles = mergeStyleSets({
  contextAreaText: {
    textAlign: "left",
  },
  link: {
    color: theme.palette.black,
    textDecoration: "none",
  },
});

export default EV2Experiments;
